import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr, fmtMoney, fmtDate, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, SectionCard } from "@/components/Bits";
import { UploadButton } from "@/components/Upload";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { FileText, Printer, Zap, Building2, BarChart3, Download, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import { EmpTypeBadge } from "./Employees";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const sgd = (n) => "S$" + Number(n || 0).toLocaleString("en-SG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function YtdReport({ open, onOpenChange }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);

  const load = async (y) => {
    try { const r = await api.get(`/reports-payroll/ytd?year=${y}`); setData(r.data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { if (open) load(year); /* eslint-disable-next-line */ }, [open, year]);

  const exportCsv = async () => {
    const r = await api.get(`/reports-payroll/ytd?year=${year}&fmt=csv`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `payroll_ytd_${year}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Year-to-Date Payroll Report (IRAS-style)</DialogTitle></DialogHeader>
        <div className="flex items-center gap-3">
          <Label>Year</Label>
          <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || year)} className="w-28 h-9 px-3 rounded-md border border-input bg-background" data-testid="ytd-year" />
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="ytd-export"><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
        </div>
        <div className="overflow-x-auto">
          {data && data.by_company && data.by_company.length > 0 && (
            <div className="mb-4">
              <p className="overline mb-2">By Company</p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead className="text-right">Staff</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Total CPF</TableHead>
                    <TableHead className="text-right">SHG</TableHead>
                    <TableHead className="text-right">SDL</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.by_company.map((c) => (
                    <TableRow key={c.payroll_company_id || c.company_name} data-testid={`ytd-company-${c.payroll_company_id || "none"}`}>
                      <TableCell className="font-medium">{c.company_name}</TableCell>
                      <TableCell className="text-right font-mono-num">{c.employee_count}</TableCell>
                      <TableCell className="text-right font-mono-num">{fmtMoney(c.gross)}</TableCell>
                      <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(c.total_cpf)}</TableCell>
                      <TableCell className="text-right font-mono-num">{fmtMoney(c.shg)}</TableCell>
                      <TableCell className="text-right font-mono-num">{fmtMoney(c.sdl)}</TableCell>
                      <TableCell className="text-right font-mono-num font-bold">{fmtMoney(c.net)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Employee</TableHead>
                <TableHead className="text-right">Months</TableHead>
                <TableHead className="text-right">Gross</TableHead>
                <TableHead className="text-right">Emp CPF</TableHead>
                <TableHead className="text-right">Empr CPF</TableHead>
                <TableHead className="text-right">Total CPF</TableHead>
                <TableHead className="text-right">SHG</TableHead>
                <TableHead className="text-right">SDL</TableHead>
                <TableHead className="text-right">Net</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(!data || data.rows.length === 0) && <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8">No payslips for {year}.</TableCell></TableRow>}
              {data && data.rows.map((r) => (
                <TableRow key={`${r.payroll_company_id || "none"}-${r.employee_id}`} data-testid={`ytd-row-${r.employee_id}`}>
                  <TableCell className="text-muted-foreground">{r.company_name}</TableCell>
                  <TableCell className="font-medium">{r.employee_name}</TableCell>
                  <TableCell className="text-right font-mono-num">{r.months}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtMoney(r.gross)}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtMoney(r.employee_cpf)}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtMoney(r.employer_cpf)}</TableCell>
                  <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(r.total_cpf)}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtMoney(r.shg)}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtMoney(r.sdl)}</TableCell>
                  <TableCell className="text-right font-mono-num font-bold">{fmtMoney(r.net)}</TableCell>
                </TableRow>
              ))}
              {data && data.rows.length > 0 && (
                <TableRow className="bg-muted font-semibold">
                  <TableCell>Total</TableCell><TableCell /><TableCell />
                  <TableCell className="text-right font-mono-num">{fmtMoney(data.totals.gross)}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtMoney(data.totals.employee_cpf)}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtMoney(data.totals.employer_cpf)}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtMoney(data.totals.total_cpf)}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtMoney(data.totals.shg)}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtMoney(data.totals.sdl)}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtMoney(data.totals.net)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CompanySummary({ open, onOpenChange }) {
  const now = new Date();
  const [sel, setSel] = useState({ month: now.getMonth() + 1, year: now.getFullYear() });
  const [data, setData] = useState(null);
  const load = async () => {
    try { const r = await api.get(`/reports-payroll/company-summary?month=${parseInt(sel.month)}&year=${parseInt(sel.year)}`); setData(r.data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, sel.month, sel.year]);
  const cols = [["gross", "Gross"], ["employee_cpf", "Emp CPF"], ["employer_cpf", "Empr CPF"], ["total_cpf", "Total CPF"], ["shg", "SHG"], ["sdl", "SDL"], ["net", "Net"]];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Payroll Summary by Company</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Month-end totals per payroll company — hand each entity its own figures.</p>
        <div className="flex items-center gap-3">
          <Label>Month</Label>
          <Select value={String(sel.month)} onValueChange={(v) => setSel({ ...sel, month: v })}>
            <SelectTrigger className="w-32" data-testid="cs-month"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover">{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Label>Year</Label>
          <input type="number" value={sel.year} onChange={(e) => setSel({ ...sel, year: parseInt(e.target.value) || sel.year })} className="w-28 h-9 px-3 rounded-md border border-input bg-background" data-testid="cs-year" />
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead className="text-right">Staff</TableHead>
                {cols.map(([k, l]) => <TableHead key={k} className="text-right">{l}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(!data || data.rows.length === 0) && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No payslips for {MONTHS[sel.month - 1]} {sel.year}.</TableCell></TableRow>}
              {data && data.rows.map((r) => (
                <TableRow key={r.payroll_company_id || "none"} data-testid={`cs-row-${r.payroll_company_id || "none"}`}>
                  <TableCell className="font-medium">{r.company_name}</TableCell>
                  <TableCell className="text-right font-mono-num">{r.employee_count}</TableCell>
                  {cols.map(([k]) => <TableCell key={k} className={`text-right font-mono-num ${k === "net" ? "font-bold" : k === "total_cpf" ? "font-semibold" : ""}`}>{fmtMoney(r[k])}</TableCell>)}
                </TableRow>
              ))}
              {data && data.rows.length > 0 && (
                <TableRow className="bg-muted font-semibold">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right font-mono-num">{data.totals.employee_count}</TableCell>
                  {cols.map(([k]) => <TableCell key={k} className="text-right font-mono-num">{fmtMoney(data.totals[k])}</TableCell>)}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Payslips() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [slips, setSlips] = useState([]);
  const [view, setView] = useState(null);
  const [settings, setSettings] = useState({ company_name: "OKRStaff", logo_file_id: null, address: "Singapore", payslip_note: "Confidential Payslip", payslip_footer: "" });
  const now = new Date();
  const [gen, setGen] = useState({ month: now.getMonth() + 1, year: now.getFullYear() });
  const [periodType, setPeriodType] = useState("monthly");   // monthly | weekly (freelancers)
  const [weekStart, setWeekStart] = useState(new Date(Date.now() - 6 * 864e5).toISOString().slice(0, 10));
  const [weekEnd, setWeekEnd] = useState(new Date().toISOString().slice(0, 10));
  const [hoursMap, setHoursMap] = useState({});             // employee_id -> hours worked that week
  const [genOpen, setGenOpen] = useState(false);
  const [ytdOpen, setYtdOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [empList, setEmpList] = useState([]);
  const [genCompany, setGenCompany] = useState("all"); // payroll company filter in the picker
  const [genSelected, setGenSelected] = useState([]);
  const [genSearch, setGenSearch] = useState("");
  const [histCompany, setHistCompany] = useState("all");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfSel, setPdfSel] = useState({ month: now.getMonth() + 1, year: now.getFullYear(), company_id: "all" });
  const [pdfBusy, setPdfBusy] = useState(false);
  const [histStatus, setHistStatus] = useState("all");       // all | draft | finalized
  const [histSelected, setHistSelected] = useState([]);      // draft payslip ids selected to credit
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [creditDate, setCreditDate] = useState(new Date().toISOString().slice(0, 10));
  const [finalizing, setFinalizing] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const load = useCallback(() => api.get("/payslips").then((r) => setSlips(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (isAdmin) api.get("/payroll-companies").then((r) => setCompanies(r.data)).catch(() => {}); }, [isAdmin]);
  useEffect(() => { if (isAdmin) api.get("/employees?status=Active").then((r) => setEmpList(r.data)).catch(() => {}); }, [isAdmin]);

  const selectedIds = () => genSelected;
  const buildPayload = () => {
    const p = { month: parseInt(gen.month), year: parseInt(gen.year), period_type: periodType };
    const ids = selectedIds();
    if (ids) p.employee_ids = ids;
    if (genCompany !== "all") p.payroll_company_id = genCompany;
    if (periodType === "weekly") {
      p.start_date = weekStart;
      p.end_date = weekEnd;
      p.hours = Object.fromEntries(genSelected.map((id) => [id, parseFloat(hoursMap[id] || 0)]));
    }
    return p;
  };
  const runPreview = async () => {
    if (genSelected.length === 0) { toast.error("Select at least one employee"); return; }
    if (periodType === "weekly" && (!weekStart || !weekEnd || weekEnd < weekStart)) { toast.error("Pick a valid week start and end date"); return; }
    setPreviewing(true);
    try {
      const { data } = await api.post("/payslips/preview", buildPayload());
      setPreview(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setPreviewing(false); }
  };

  const generate = async () => {
    try {
      const { data } = await api.post("/payslips/generate", buildPayload());
      toast.success(`Saved ${data.count} payslip(s) as Pending Credit`);
      setGenOpen(false); setPreview(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const downloadBankCsv = () => {
    if (!preview || preview.payslips.length === 0) { toast.error("Run a preview first"); return; }
    const rows = [["Employee", "Bank", "Account Number", "Net Amount"]];
    preview.payslips.forEach((p) => rows.push([p.employee_name, p.bank_name || "", p.bank_account_number || "", Number(p.net_pay || 0).toFixed(2)]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `bank_transfer_${gen.year}-${String(gen.month).padStart(2, "0")}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const doFinalize = async () => {
    if (!creditDate) { toast.error("Pick a salary credit date"); return; }
    setFinalizing(true);
    try {
      const { data } = await api.post("/payslips/finalize", { payslip_ids: selectedDraftIds, salary_credit_date: creditDate });
      toast.success(`${data.finalized} payslip(s) credited & issued to staff`);
      setFinalizeOpen(false); setHistSelected([]); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setFinalizing(false); }
  };

  const downloadSheetCsv = () => {
    const sel = slips.filter((s) => histSelected.includes(s.id));
    if (sel.length === 0) return;
    const rows = [["Employee", "Bank", "Account Number", "Gross Salary", "Employee CPF", "Employer CPF", "Net to Credit"]];
    sel.forEach((s) => rows.push([s.employee_name, s.bank_name || "", s.bank_account_number || "", Number(s.base_salary || 0).toFixed(2), Number(s.employee_cpf || 0).toFixed(2), Number(s.employer_cpf || 0).toFixed(2), Number(s.net_pay || 0).toFixed(2)]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `salary_credit_sheet.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const openSlip = async (id) => {
    const { data } = await api.get(`/payslips/${id}`);
    setView(data);
  };

  const sheetSlips = slips.filter((s) => histSelected.includes(s.id) && (s.status || "finalized") === "draft");
  const sheetTotal = sheetSlips.reduce((a, s) => a + (s.net_pay || 0), 0);
  const sheetMonths = [...new Set(sheetSlips.map((s) => `${MONTHS[s.month - 1]} ${s.year}`))];

  const visibleSlips = slips
    .filter((s) => histCompany === "all" || s.payroll_company_id === histCompany)
    .filter((s) => histStatus === "all" || (s.status || "finalized") === histStatus);
  const visibleIds = visibleSlips.map((s) => s.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => histSelected.includes(id));
  const selectedDraftIds = slips.filter((s) => histSelected.includes(s.id) && (s.status || "finalized") === "draft").map((s) => s.id);

  const genFiltered = empList.filter((e) => {
    const okSearch = e.full_name.toLowerCase().includes(genSearch.toLowerCase());
    const cos = (e.payroll_assignments && e.payroll_assignments.length) ? e.payroll_assignments.map((a) => a.payroll_company_id) : [e.payroll_company_id];
    const okCo = genCompany === "all" || cos.includes(genCompany);
    const okType = periodType !== "weekly" || e.employment_type === "Freelance";
    return okSearch && okCo && okType;
  });
  const genFilteredIds = genFiltered.map((e) => e.id);
  const allGenSelected = genFilteredIds.length > 0 && genFilteredIds.every((id) => genSelected.includes(id));

  const downloadPdfPack = async () => {
    setPdfBusy(true);
    try {
      const co = pdfSel.company_id && pdfSel.company_id !== "all" ? `&company_id=${pdfSel.company_id}` : "";
      const r = await api.get(`/payslips/export-pdf?month=${parseInt(pdfSel.month)}&year=${parseInt(pdfSel.year)}${co}`, { responseType: "blob" });
      const coName = pdfSel.company_id !== "all" ? (companies.find((c) => c.id === pdfSel.company_id)?.name || "company").replace(/[^A-Za-z0-9_-]+/g, "_") + "_" : "";
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `payslips_${coName}${pdfSel.year}-${String(pdfSel.month).padStart(2, "0")}.zip`; a.click(); URL.revokeObjectURL(url);
      toast.success("Payslip PDF pack downloaded");
    } catch (e) {
      const msg = e?.response?.status === 404 ? "No payslips for that selection — generate them first." : apiErr(e);
      toast.error(msg);
    } finally { setPdfBusy(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete the ${MONTHS[s.month - 1]} ${s.year} payslip for ${s.employee_name}? You can regenerate it later.`)) return;
    try { await api.delete(`/payslips/${s.id}`); toast.success("Payslip deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const bulkDelete = async () => {
    if (!histSelected.length) return;
    if (!window.confirm(`Delete ${histSelected.length} selected payslip(s)? This cannot be undone. You can regenerate them later.`)) return;
    try {
      const { data } = await api.post("/payslips/bulk-delete", { payslip_ids: histSelected });
      toast.success(`Deleted ${data.deleted} payslip(s)`);
      setHistSelected([]); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const exportZip = async (ids) => {
    try {
      const r = await api.post("/payslips/export-pdf-zip", { payslip_ids: ids || null }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a"); a.href = url; a.download = "payslips.zip"; a.click(); URL.revokeObjectURL(url);
      toast.success(`Downloaded ${ids ? ids.length : "all"} payslip(s) as ZIP`);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "No monthly payslips to export." : "Could not export payslips.");
    }
  };

  const printCombined = async (ids) => {
    try {
      const r = await api.post("/payslips/export-pdf-combined", { payslip_ids: ids || null }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank");
      if (w) { w.onload = () => { try { w.focus(); w.print(); } catch (_) {} }; }
      else { const a = document.createElement("a"); a.href = url; a.download = "payslips_combined.pdf"; a.click(); }
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "No monthly payslips to print." : "Could not build combined PDF.");
    }
  };

  return (
    <div className="animate-fade-up">
      <PageTitle overline="Payroll" title="Payslips">
        {isAdmin && (
          <Button variant="outline" onClick={() => setYtdOpen(true)} data-testid="ytd-report-btn"><BarChart3 className="h-4 w-4 mr-1.5" /> YTD Report</Button>
        )}
        {isAdmin && (
          <Button variant="outline" onClick={() => setSummaryOpen(true)} data-testid="company-summary-btn"><Building2 className="h-4 w-4 mr-1.5" /> Company Summary</Button>
        )}
        {isAdmin && (
          <Button variant="outline" onClick={async () => {
            const r = await api.get("/reports/payslips", { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a"); a.href = url; a.download = "payslips.csv"; a.click(); URL.revokeObjectURL(url);
          }} data-testid="export-payslips-csv"><FileText className="h-4 w-4 mr-1.5" /> Export CSV</Button>
        )}
        {isAdmin && (
          <Dialog open={pdfOpen} onOpenChange={setPdfOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="pdf-pack-btn"><Download className="h-4 w-4 mr-1.5" /> PDF Pack</Button></DialogTrigger>
            <DialogContent className="bg-popover max-w-sm">
              <DialogHeader><DialogTitle className="font-heading">Download Payslip PDF Pack</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">Bundles generated payslips for the selected month into a ZIP, organised in a folder per payroll company.</p>
              <div className="grid grid-cols-2 gap-3 py-2">
                <div>
                  <Label>Month</Label>
                  <Select value={String(pdfSel.month)} onValueChange={(v) => setPdfSel({ ...pdfSel, month: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="pdf-month"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover">{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Year</Label><input className="mt-1.5 w-full h-10 px-3 rounded-md border border-input bg-background" type="number" value={pdfSel.year} onChange={(e) => setPdfSel({ ...pdfSel, year: e.target.value })} data-testid="pdf-year" /></div>
                <div className="col-span-2">
                  <Label>Payroll Company</Label>
                  <Select value={pdfSel.company_id} onValueChange={(v) => setPdfSel({ ...pdfSel, company_id: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="pdf-company"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover">
                      <SelectItem value="all">All companies</SelectItem>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button onClick={downloadPdfPack} disabled={pdfBusy} data-testid="download-pdf-pack"><Download className="h-4 w-4 mr-1.5" /> {pdfBusy ? "Preparing…" : "Download ZIP"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {isAdmin && (
          <Dialog open={genOpen} onOpenChange={(o) => { setGenOpen(o); if (!o) setPreview(null); }}>
            <DialogTrigger asChild><Button data-testid="generate-payslip-btn"><Zap className="h-4 w-4 mr-1.5" /> Generate Payslips</Button></DialogTrigger>
            <DialogContent className="bg-popover max-w-5xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">Generate Payslips</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">Preview payslips for the selected period, review pay & bank details, then <b>save as Pending Credit</b>. They stay admin-only until you credit salaries from the history below.</p>
              <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/40 w-fit" data-testid="period-type-toggle">
                {["monthly", "weekly"].map((t) => (
                  <button key={t} onClick={() => { setPeriodType(t); setPreview(null); setGenSelected([]); }} className={`px-3 py-1.5 text-sm rounded ${periodType === t ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid={`period-type-${t}`}>
                    {t === "monthly" ? "Monthly" : "Weekly (Freelancers)"}
                  </button>
                ))}
              </div>
              <div className="flex items-end gap-3 py-2">
                {periodType === "monthly" ? (
                  <>
                    <div>
                      <Label>Month</Label>
                      <Select value={String(gen.month)} onValueChange={(v) => { setGen({ ...gen, month: v }); setPreview(null); }}>
                        <SelectTrigger className="mt-1.5 w-32" data-testid="gen-month"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-popover">{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Year</Label><input className="mt-1.5 w-28 h-10 px-3 rounded-md border border-input bg-background" type="number" value={gen.year} onChange={(e) => { setGen({ ...gen, year: e.target.value }); setPreview(null); }} data-testid="gen-year" /></div>
                  </>
                ) : (
                  <>
                    <div><Label>Week Start</Label><input className="mt-1.5 w-40 h-10 px-3 rounded-md border border-input bg-background" type="date" value={weekStart} onChange={(e) => { setWeekStart(e.target.value); setPreview(null); }} data-testid="gen-week-start" /></div>
                    <div><Label>Week End</Label><input className="mt-1.5 w-40 h-10 px-3 rounded-md border border-input bg-background" type="date" value={weekEnd} onChange={(e) => { setWeekEnd(e.target.value); setPreview(null); }} data-testid="gen-week-end" /></div>
                  </>
                )}
                <Button variant="outline" onClick={runPreview} disabled={previewing} data-testid="preview-payslips-btn"><Eye className="h-4 w-4 mr-1.5" /> {previewing ? "Loading…" : "Preview"}</Button>
              </div>
              {periodType === "weekly" && <p className="text-xs text-muted-foreground -mt-1">Freelancers only. Gross = hourly rate × hours worked (enter hours per person below). No CPF/SDL — gross = net.</p>}

              <div className="rounded-md border border-border p-3">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <Select value={genCompany} onValueChange={(v) => { setGenCompany(v); setPreview(null); }}>
                    <SelectTrigger className="h-9 w-56" data-testid="gen-company-filter"><SelectValue placeholder="All companies" /></SelectTrigger>
                    <SelectContent className="bg-popover">
                      <SelectItem value="all">All payroll companies</SelectItem>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <input className="flex-1 min-w-[160px] h-9 px-3 rounded-md border border-input bg-background text-sm" placeholder="Search employees…" value={genSearch} onChange={(e) => setGenSearch(e.target.value)} data-testid="gen-emp-search" />
                  <span className="text-xs text-muted-foreground">{genSelected.length} selected</span>
                  <Button size="sm" variant="ghost" onClick={() => { setGenSelected([]); setPreview(null); }} data-testid="gen-clear-sel">Clear</Button>
                </div>
                <div className="max-h-56 overflow-y-auto border border-border rounded-md divide-y divide-border" data-testid="gen-emp-list">
                  <label className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-muted/60 cursor-pointer sticky top-0" data-testid="gen-select-all">
                    <input type="checkbox" checked={allGenSelected} onChange={() => { setGenSelected((p) => allGenSelected ? p.filter((id) => !genFilteredIds.includes(id)) : [...new Set([...p, ...genFilteredIds])]); setPreview(null); }} />
                    Select all{genCompany !== "all" ? " in this company" : ""} ({genFilteredIds.length})
                  </label>
                  {genFiltered.map((e) => {
                    const on = genSelected.includes(e.id);
                    return (
                      <label key={e.id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-accent" data-testid={`gen-emp-${e.id}`}>
                        <input type="checkbox" checked={on} onChange={() => { setGenSelected((p) => on ? p.filter((x) => x !== e.id) : [...p, e.id]); setPreview(null); }} />
                        {e.full_name}<span className="text-xs text-muted-foreground ml-auto">{e.role}</span>
                        {periodType === "weekly" && on && (
                          <input type="number" min="0" step="0.5" className="w-24 h-7 px-2 rounded border border-input bg-background text-xs" placeholder="Hours" value={hoursMap[e.id] ?? ""} onClick={(ev) => ev.preventDefault()} onChange={(ev) => { setHoursMap((m) => ({ ...m, [e.id]: ev.target.value })); setPreview(null); }} data-testid={`gen-hours-${e.id}`} />
                        )}
                      </label>
                    );
                  })}
                  {genFiltered.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">{periodType === "weekly" ? "No freelancers match." : "No employees match."}</div>}
                </div>
              </div>

              {preview && (
                <div className="overflow-x-auto border border-border rounded-md" data-testid="payslip-preview-table">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Payroll Company</TableHead>
                      <TableHead className="text-right">Base</TableHead>
                      <TableHead className="text-right">Comm.</TableHead>
                      <TableHead className="text-right">Bonus</TableHead>
                      <TableHead className="text-right">Claims</TableHead>
                      <TableHead className="text-right">Gross</TableHead>
                      <TableHead className="text-right">Emp CPF</TableHead>
                      <TableHead className="text-right">Employer CPF</TableHead>
                      <TableHead className="text-right">SDL</TableHead>
                      <TableHead className="text-right">Nett Salary</TableHead>
                      <TableHead>Bank</TableHead>
                      <TableHead>Account No.</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {preview.payslips.length === 0 && <TableRow><TableCell colSpan={13} className="text-center text-muted-foreground py-6">No active employees.</TableCell></TableRow>}
                      {preview.payslips.map((p) => (
                        <TableRow key={`${p.employee_id}-${p.payroll_company_id || "none"}`} data-testid={`preview-row-${p.employee_id}`}>
                          <TableCell className="font-medium">{p.employee_name}<EmpTypeBadge type={p.employment_type} className="ml-1.5 align-middle" />{p.already_generated && <span className="ml-1.5 text-[10px] text-warning">(will overwrite)</span>}{p.prorated && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-warning/15 text-warning font-medium align-middle" title={`Pro-rated: ${p.days_worked}/${p.total_working_days} working days · full base ${fmtMoney(p.full_base_salary)}`} data-testid={`prorate-badge-${p.employee_id}`}>Pro-rated {p.days_worked}/{p.total_working_days}</span>}</TableCell>
                          <TableCell className="text-muted-foreground">{p.company?.name || "Default company"}</TableCell>
                          <TableCell className="text-right font-mono-num">{fmtMoney(p.base_salary)}</TableCell>
                          <TableCell className="text-right font-mono-num">{fmtMoney(p.commissions)}</TableCell>
                          <TableCell className="text-right font-mono-num" data-testid={`preview-bonus-${p.employee_id}`}>{p.bonus > 0 ? fmtMoney(p.bonus) : "—"}</TableCell>
                          <TableCell className="text-right font-mono-num">{p.claims > 0 ? <span className="text-success">{fmtMoney(p.claims)}</span> : "—"}</TableCell>
                          <TableCell className="text-right font-mono-num">{fmtMoney(p.gross_pay)}</TableCell>
                          <TableCell className="text-right font-mono-num text-muted-foreground">{fmtMoney(p.employee_cpf)}</TableCell>
                          <TableCell className="text-right font-mono-num text-muted-foreground">{fmtMoney(p.employer_cpf)}</TableCell>
                          <TableCell className="text-right font-mono-num text-muted-foreground">{fmtMoney(p.sdl)}</TableCell>
                          <TableCell className="text-right font-mono-num font-bold">{fmtMoney(p.net_pay)}</TableCell>
                          <TableCell className="text-sm">{p.bank_name || "—"}</TableCell>
                          <TableCell className="font-mono-num text-sm">{p.bank_account_number || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <DialogFooter>
                {preview && <Button variant="outline" onClick={downloadBankCsv} data-testid="download-bank-csv"><Download className="h-4 w-4 mr-1.5" /> Bank Transfer CSV</Button>}
                <Button onClick={generate} disabled={!preview} data-testid="confirm-generate"><FileText className="h-4 w-4 mr-1.5" /> Save as Pending Credit{preview ? ` (${preview.count})` : ""}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageTitle>

      <SectionCard title="Payslip History" action={isAdmin ? (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => exportZip(null)} data-testid="download-all-zip"><Download className="h-4 w-4 mr-1.5" /> Download All (ZIP)</Button>
          <Button size="sm" variant="outline" onClick={() => printCombined(null)} data-testid="print-all-combined"><Printer className="h-4 w-4 mr-1.5" /> Print All</Button>
          <Select value={histStatus} onValueChange={setHistStatus}>
            <SelectTrigger className="h-8 w-40" data-testid="hist-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Pending Credit</SelectItem>
              <SelectItem value="finalized">Credited</SelectItem>
            </SelectContent>
          </Select>
          {companies.length > 0 && (
            <Select value={histCompany} onValueChange={setHistCompany}>
              <SelectTrigger className="h-8 w-56" data-testid="hist-company-filter"><SelectValue placeholder="All companies" /></SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value="all">All payroll companies</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      ) : null}>
        {isAdmin && histSelected.length > 0 && (
          <div className="flex items-center justify-between gap-3 mb-3 p-3 rounded-md bg-primary/5 border border-primary/20" data-testid="finalize-bar">
            <span className="text-sm font-medium">{histSelected.length} payslip(s) selected{selectedDraftIds.length ? ` · ${selectedDraftIds.length} pending credit` : ""}</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setHistSelected([])} data-testid="clear-hist-sel">Clear</Button>
              <Button size="sm" variant="outline" onClick={() => exportZip(histSelected)} data-testid="download-selected-zip"><Download className="h-4 w-4 mr-1.5" /> Download PDFs</Button>
              <Button size="sm" variant="outline" onClick={() => printCombined(histSelected)} data-testid="print-selected-combined"><Printer className="h-4 w-4 mr-1.5" /> Print Selected</Button>
              <Button size="sm" variant="outline" className="text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10" onClick={bulkDelete} data-testid="bulk-delete-payslips"><Trash2 className="h-4 w-4 mr-1.5" /> Delete Selected</Button>
              {selectedDraftIds.length > 0 && <Button size="sm" variant="outline" onClick={() => setSheetOpen(true)} data-testid="open-credit-sheet"><Printer className="h-4 w-4 mr-1.5" /> Credit Sheet</Button>}
              {selectedDraftIds.length > 0 && <Button size="sm" onClick={() => setFinalizeOpen(true)} data-testid="open-finalize"><Zap className="h-4 w-4 mr-1.5" /> Mark Credited & Issue</Button>}
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {isAdmin && <TableHead className="w-8">{visibleIds.length > 0 && <input type="checkbox" checked={allVisibleSelected} onChange={() => setHistSelected(allVisibleSelected ? histSelected.filter((id) => !visibleIds.includes(id)) : [...new Set([...histSelected, ...visibleIds])])} title="Select all" data-testid="select-all-payslips" />}</TableHead>}
                <TableHead>Period</TableHead>
                {isAdmin && <TableHead>Employee</TableHead>}
                <TableHead className="text-right">Base</TableHead>
                <TableHead className="text-right">Nett Salary</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Credit Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {slips.length === 0 && <TableRow><TableCell colSpan={isAdmin ? 8 : 5} className="text-center text-muted-foreground py-8">No payslips yet.</TableCell></TableRow>}
              {visibleSlips.map((s) => {
                const isDraft = (s.status || "finalized") === "draft";
                return (
                <TableRow key={s.id} data-testid={`payslip-row-${s.id}`}>
                  {isAdmin && <TableCell><input type="checkbox" checked={histSelected.includes(s.id)} onChange={() => setHistSelected((p) => p.includes(s.id) ? p.filter((x) => x !== s.id) : [...p, s.id])} data-testid={`sel-payslip-${s.id}`} /></TableCell>}
                  <TableCell className="font-medium">{MONTHS[s.month - 1]} {s.year}</TableCell>
                  {isAdmin && <TableCell>{s.employee_name}</TableCell>}
                  <TableCell className="text-right font-mono-num">{fmtMoney(s.base_salary)}</TableCell>
                  <TableCell className="text-right font-mono-num font-bold">{fmtMoney(s.net_pay)}</TableCell>
                  <TableCell>
                    {isDraft
                      ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-warning/15 text-warning font-medium" data-testid={`status-${s.id}`}>Pending Credit</span>
                      : <span className="text-[11px] px-2 py-0.5 rounded-full bg-success/15 text-success font-medium" data-testid={`status-${s.id}`}>Credited</span>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{s.salary_credit_date ? fmtDate(s.salary_credit_date) : "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => openSlip(s.id)} data-testid={`view-payslip-${s.id}`}><FileText className="h-4 w-4 mr-1" /> View</Button>
                      {isAdmin && <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => del(s)} data-testid={`delete-payslip-${s.id}`}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </SectionCard>

      {/* Payslip viewer */}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="bg-popover max-w-2xl max-h-[90vh] overflow-y-auto">
          {view && (
            <>
              <div id="payslip-print" className="bg-white text-[#0A0A0A] p-2">
                {/* Header — logo left + company block, left-aligned (matches PDF) */}
                <div className="flex items-start gap-3">
                  {(view.company?.logo_file_id || settings.logo_file_id)
                    ? <img src={fileUrl(view.company?.logo_file_id || settings.logo_file_id)} alt="logo" className="max-h-[24mm] w-auto max-w-[52mm] object-contain shrink-0" />
                    : <Building2 className="h-20 w-20 text-muted-foreground shrink-0" />}
                  <div>
                    <p className="font-heading font-extrabold text-lg leading-tight">{view.company?.name || settings.company_name}{(view.company?.uen) ? ` (UEN ${view.company.uen})` : ""}</p>
                    {(view.company?.address) && <p className="text-[11px] text-[#666]">{view.company.address}</p>}
                    {(view.company?.website || view.company?.contact_email) && <p className="text-[11px] text-[#666]">{[view.company?.website, view.company?.contact_email].filter(Boolean).join(" · ")}</p>}
                    <p className="text-[11px] text-[#666]">{view.company?.payslip_note || settings.payslip_note || "Confidential Payslip"}</p>
                  </div>
                </div>

                {/* Period + payment line */}
                <div className="mt-4">
                  <p className="text-sm"><span className="font-bold">Payslip — {MONTHS[view.month - 1]} {view.year}</span>{(view.salary_period_start && view.salary_period_end) ? <span className="ml-3">Salary period: {fmtDate(view.salary_period_start)} – {fmtDate(view.salary_period_end)}</span> : null}</p>
                  <p className="text-[11px] text-[#666]">Payment method: {view.payment_method || "Bank Transfer"}</p>
                  {view.salary_credit_date && <p className="text-[11px] text-[#666]" data-testid="payslip-credit-date">Salary credit date: {fmtDate(view.salary_credit_date)}</p>}
                  {view.prorated && <p className="text-[11px] text-warning font-medium" data-testid="payslip-prorate">Pro-rated salary: {view.days_worked} / {view.total_working_days} working days (base {sgd(view.full_base_salary)})</p>}
                </div>

                {/* Employee info — inline-label table matching PDF column order */}
                <table className="w-full mt-4 text-sm">
                  <tbody>
                    <tr><td className="py-0.5 text-[#888] w-[20%]">Employee</td><td className="py-0.5 w-[30%] font-medium">{view.employee_name}</td><td className="py-0.5 text-[#888] w-[20%]">NRIC / FIN</td><td className="py-0.5 font-mono-num">{view.nric_fin || "—"}</td></tr>
                    <tr><td className="py-0.5 text-[#888]">Role</td><td className="py-0.5">{view.role}</td><td className="py-0.5 text-[#888]">Department</td><td className="py-0.5">{view.department}</td></tr>
                    <tr><td className="py-0.5 text-[#888]">Division</td><td className="py-0.5">{view.division}</td><td className="py-0.5 text-[#888]">Clock Hours</td><td className="py-0.5 font-mono-num">{view.clock_hours}h</td></tr>
                  </tbody>
                </table>

                {/* Earnings & Deductions */}
                <table className="w-full mt-4 text-sm border-collapse">
                  <tbody>
                    <tr><td colSpan={2} className="bg-[#111827] text-white font-heading font-bold px-3 py-2">Earnings &amp; Deductions</td></tr>
                    {[
                      ["Base Salary", sgd(view.base_salary)],
                      ["Commissions", sgd(view.commissions)],
                      ...(view.bonus > 0 ? [["Bonus (no CPF/SDL)", sgd(view.bonus)]] : []),
                      ["Reimbursement", sgd(view.reimbursement)],
                      ...(view.claims > 0 ? [["Claims (approved)", sgd(view.claims)]] : []),
                      ...((view.allowances || []).map((a) => [
                        a.full && Math.abs((a.full || 0) - (a.amount || 0)) > 0.001 ? `${a.label} (pro-rated from ${sgd(a.full)})` : a.label,
                        sgd(a.amount),
                      ])),
                      ["Gross Pay", sgd(view.gross_pay)],
                      ...(view.salary_advance > 0 ? [["Salary Advance (payout)", sgd(view.salary_advance)]] : []),
                      ...((view.deduction_items && view.deduction_items.length)
                        ? view.deduction_items.map((d) => [d.label, `- ${sgd(d.amount)}`])
                        : (view.deductions > 0 ? [["Other Deductions", `- ${sgd(view.deductions)}`]] : [])),
                      [`Self-Help Group (${view.shg_group || "None"})`, `- ${sgd(view.shg_contribution || 0)}`],
                      ["Employee CPF", `- ${sgd(view.employee_cpf || 0)}`],
                      ["Employer CPF", sgd(view.employer_cpf || 0)],
                      ["Total CPF", sgd((view.employee_cpf || 0) + (view.employer_cpf || 0))],
                      ["SDL", sgd(view.sdl || 0)],
                    ].map(([k, v], idx) => (
                      <tr key={`${k}-${idx}`} className="border-b border-[#e5e7eb]"><td className="px-3 py-1.5">{k}</td><td className="px-3 py-1.5 text-right font-mono-num">{v}</td></tr>
                    ))}
                    <tr className="bg-[#2563eb] text-white"><td className="px-3 py-2.5 font-heading font-bold">Nett Salary</td><td className="px-3 py-2.5 text-right font-mono-num font-bold">{sgd(view.net_pay)}</td></tr>
                  </tbody>
                </table>

                {(view.claim_items || []).length > 0 && (
                  <table className="w-full mt-3 text-xs">
                    <tbody>
                      <tr><td colSpan={2} className="text-[#888] font-semibold py-1">Claims breakdown</td></tr>
                      {view.claim_items.map((ci, i) => (
                        <tr key={i}><td className="py-0.5 text-[#666]">{ci.category}</td><td className="py-0.5 text-right font-mono-num">{sgd(ci.amount)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {(view.commission_deals || []).length > 0 && (
                  <table className="w-full mt-3 text-xs">
                    <tbody>
                      <tr><td colSpan={2} className="text-[#888] font-semibold py-1">Commission breakdown</td></tr>
                      {view.commission_deals.map((d, i) => (
                        <tr key={i}><td className="py-0.5 text-[#666]">{d.title || "Deal"}</td><td className="py-0.5 text-right font-mono-num">{sgd(d.value)}</td></tr>
                      ))}
                      <tr className="border-t border-[#e5e7eb]"><td className="py-0.5 font-semibold">Total commission</td><td className="py-0.5 text-right font-mono-num font-semibold">{sgd(view.commissions)}</td></tr>
                    </tbody>
                  </table>
                )}
                {view.credit_screenshot_id && (
                  <div className="mt-4"><p className="text-[#888] text-xs font-semibold mb-1">Credit Transfer Proof</p><img src={fileUrl(view.credit_screenshot_id)} alt="credit proof" className="max-h-48 rounded-md border border-border" /></div>
                )}
                <div className="mt-5 grid grid-cols-2 gap-6">
                  <div data-testid="payslip-stamp">
                    {view.company?.stamp_file_id
                      ? <img src={fileUrl(view.company.stamp_file_id)} alt="company stamp" className="max-h-24 object-contain mb-1" />
                      : <div className="h-24 rounded-md border border-dashed border-border mb-1" />}
                    <p className="text-[#888] text-xs font-semibold">Company Stamp</p>
                  </div>
                  {view.payment_method === "Cash" && (
                    <div data-testid="payslip-signature">
                      <p className="text-[#888] text-xs font-semibold mb-1">Employee Signature</p>
                      <div className="h-16 border-b border-foreground/40" />
                      <p className="text-[10px] text-[#666] mt-1">I acknowledge receipt of the above salary in cash.</p>
                    </div>
                  )}
                </div>
                <p className="mt-5 pt-3 border-t border-[#e5e7eb] text-[10px] text-[#666]">
                  {view.company?.payslip_footer || settings.payslip_footer}
                  {(view.company?.payslip_footer || settings.payslip_footer) ? <br /> : null}
                  No signature is required with stamp.
                </p>
              </div>
              <DialogFooter className="flex-row gap-2 no-print">
                {isAdmin && <UploadButton label="Upload Credit Proof" onUploaded={async (f) => { await api.put(`/payslips/${view.id}/credit`, { credit_screenshot_id: f.id }); openSlip(view.id); }} />}
                <Button onClick={() => window.print()} data-testid="print-payslip"><Printer className="h-4 w-4 mr-1.5" /> Print / PDF</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      {isAdmin && <YtdReport open={ytdOpen} onOpenChange={setYtdOpen} />}
      {isAdmin && <CompanySummary open={summaryOpen} onOpenChange={setSummaryOpen} />}
      {isAdmin && (
        <Dialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
          <DialogContent className="bg-popover max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Credit Salary & Issue Payslips</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">This marks <b>{histSelected.length}</b> pending-credit payslip(s) as salary-credited, stamps the credit date, and notifies staff by email. This makes them visible to employees.</p>
            <div className="py-2">
              <Label>Salary Credit Date</Label>
              <input type="date" value={creditDate} onChange={(e) => setCreditDate(e.target.value)} className="mt-1.5 w-full h-10 px-3 rounded-md border border-input bg-background" data-testid="credit-date-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFinalizeOpen(false)} data-testid="cancel-finalize">Cancel</Button>
              <Button onClick={doFinalize} disabled={finalizing} data-testid="confirm-finalize"><Zap className="h-4 w-4 mr-1.5" /> {finalizing ? "Issuing…" : "Confirm & Issue"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {isAdmin && (
        <Dialog open={sheetOpen} onOpenChange={setSheetOpen}>
          <DialogContent className="bg-popover max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="no-print"><DialogTitle className="font-heading">Salary Credit Sheet</DialogTitle></DialogHeader>
            <div id="payslip-print" className="bg-white text-[#0A0A0A] p-3">
              <div className="flex items-start justify-between border-b-2 border-primary pb-3 mb-3">
                <div className="flex items-center gap-3">
                  {settings.logo_file_id && <div className="h-12 w-12 rounded-md bg-white border border-border flex items-center justify-center overflow-hidden shrink-0"><img src={fileUrl(settings.logo_file_id)} alt="logo" className="h-full w-full object-contain" /></div>}
                  <div>
                    <p className="font-heading font-extrabold text-lg">{settings.company_name || "Company"} — Salary Credit Sheet</p>
                    <p className="text-xs text-muted-foreground">{sheetSlips.length} employee(s) · Generated {fmtDate(new Date().toISOString().slice(0, 10))}</p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="overline">Pay Month</p>
                  <p className="font-heading font-bold" data-testid="sheet-month">{sheetMonths.join(", ") || "—"}</p>
                </div>
              </div>
              <table className="w-full text-sm border border-border" data-testid="credit-sheet-table">
                <thead>
                  <tr className="bg-muted text-left">
                    <th className="px-2 py-2">Employee</th>
                    <th className="px-2 py-2">Bank</th>
                    <th className="px-2 py-2">Account No.</th>
                    <th className="px-2 py-2 text-right">Gross Salary</th>
                    <th className="px-2 py-2 text-right">Emp CPF</th>
                    <th className="px-2 py-2 text-right">Employer CPF</th>
                    <th className="px-2 py-2 text-right">Net to Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {sheetSlips.map((s) => (
                    <tr key={s.id} className="border-t border-border" data-testid={`sheet-row-${s.id}`}>
                      <td className="px-2 py-1.5 font-medium">{s.employee_name}</td>
                      <td className="px-2 py-1.5">{s.bank_name || "—"}</td>
                      <td className="px-2 py-1.5 font-mono-num">{s.bank_account_number || "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono-num">{fmtMoney(s.base_salary)}</td>
                      <td className="px-2 py-1.5 text-right font-mono-num">{fmtMoney(s.employee_cpf)}</td>
                      <td className="px-2 py-1.5 text-right font-mono-num">{fmtMoney(s.employer_cpf)}</td>
                      <td className="px-2 py-1.5 text-right font-mono-num font-bold">{fmtMoney(s.net_pay)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-primary bg-muted font-bold">
                    <td className="px-2 py-2" colSpan={6}>Total to Credit</td>
                    <td className="px-2 py-2 text-right font-mono-num" data-testid="sheet-total">{fmtMoney(sheetTotal)}</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-[11px] text-muted-foreground mt-2">Gross salary shown is the basic monthly salary, before any claims, commissions or deductions. Net to Credit is the final amount payable to the employee.</p>
            </div>
            <DialogFooter className="no-print">
              <Button variant="outline" onClick={downloadSheetCsv} data-testid="sheet-download-csv"><Download className="h-4 w-4 mr-1.5" /> Download CSV</Button>
              <Button onClick={() => window.print()} data-testid="sheet-print"><Printer className="h-4 w-4 mr-1.5" /> Print / PDF</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
